// EDIFACT Glossar - Detail-Seite für einzelne Begriffe
import React, { useEffect } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { LuArrowLeft, LuArrowRight, LuChevronRight, LuFileSpreadsheet } from "react-icons/lu";
import SeoHead from "../../components/SeoHead";
import { glossarTermMeta } from "../../data/pageMeta";
import TopBanner from "../../components/TopBanner";
import { TOOLS } from "../../components/tools";
import { getTermBySlug } from "../../data/glossaryTerms";

// Tool-Links aus den Glossardaten (alte Pfade werden auf die echten Routen abgebildet)
const TOOL_ALIASES = { "/edifact-konverter": "/edi-konverter" };
const EXTRA_TOOLS = {
  "/edifact-excel-konverter": { name: "EDIFACT → Excel Konverter", icon: LuFileSpreadsheet },
};

export default function GlossarDetail() {
  const { term: slug } = useParams();
  
  // Scroll nach oben bei jedem Begriff-Wechsel
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);
  
  // Sanitize URL parameter (XSS-Prevention)
  const sanitizedSlug = slug ? String(slug).toLowerCase().replace(/[^a-z0-9-]/g, '') : '';
  const term = getTermBySlug(sanitizedSlug);

  // 404 wenn Begriff nicht gefunden
  if (!term) {
    return <Navigate to="/glossar" replace />;
  }

  // Schema.org Structured Data für einzelnen Begriff
  // Nur Pre-Defined Content - kein User-Input!
  const schemaData = {
    "@context": "https://schema.org",
    "@type": "DefinedTerm",
    "name": String(term.term).substring(0, 100),
    "description": String(term.description).substring(0, 500),
    "url": `https://edi-converter.de/glossar/${encodeURIComponent(term.slug)}`,
    "termCode": String(term.term).substring(0, 50),
    "inDefinedTermSet": {
      "@type": "DefinedTermSet",
      "name": "EDIFACT Glossar",
      "url": "https://edi-converter.de/glossar"
    },
    "identifier": String(term.slug).substring(0, 100),
    "inLanguage": "de"
  };

  // Falls Beispiel vorhanden, als "example" hinzufügen (mit Length-Limit)
  if (term.example) {
    schemaData.example = {
      "@type": "CreativeWork",
      "text": String(term.example).substring(0, 500)
    };
  }

  return (
    <>
      <SeoHead
        {...glossarTermMeta(term)}
        keywords={`${term.term}, ${term.fullName}, EDIFACT ${term.category}, EDI ${term.term}`}
        ogUrl={`https://edi-converter.de/glossar/${term.slug}`}
      />

      {/* Schema.org Structured Data - Safe: nur Pre-Defined Content */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{
        __html: JSON.stringify(schemaData)
      }} />

      <TopBanner />

      <section className="page-hero">
        <div className="container">
          <nav className="breadcrumb" aria-label="Brotkrumen">
            <Link to="/glossar">Glossar</Link>
            <LuChevronRight size={14} aria-hidden="true" />
            <span>{term.category}</span>
            <LuChevronRight size={14} aria-hidden="true" />
            <span aria-current="page">{term.term}</span>
          </nav>
          <div className="row" style={{ gap: 14 }}>
            <h1 className="h1 mono" style={{ letterSpacing: "-0.02em" }}>{term.term}</h1>
            <span className="badge">{term.category}</span>
          </div>
          <h2 className="lead" style={{ fontSize: "1.25rem", fontWeight: 500, color: "var(--text-body)" }}>{term.fullName}</h2>
        </div>
      </section>

      <section className="section-sm">
        <div className="container">
          <div className="glossar-layout">
            <div className="stack-lg" style={{ minWidth: 0 }}>
              {/* Beschreibung */}
              <div className="card card-pad-lg">
                <h3 className="h3">Beschreibung</h3>
                <p style={{ marginTop: 10, lineHeight: 1.75 }}>{term.description}</p>
              </div>

              {/* Beispiel */}
              {term.example && (
                <div className="card card-pad-lg">
                  <h3 className="h3">Beispiel</h3>
                  <pre className="code-block" style={{ marginTop: 14, marginBottom: 0, whiteSpace: "pre-wrap", wordBreak: "break-all" }}>
{term.example}
                  </pre>
                </div>
              )}

              {/* Struktur */}
              {term.structure && (
                <div className="card card-pad-lg">
                  <h3 className="h3">Struktur</h3>
                  <pre className="code-block" style={{ marginTop: 14, marginBottom: 0, whiteSpace: "pre-wrap", wordBreak: "break-all" }}>
{term.structure}
                  </pre>
                </div>
              )}

              {/* Verwendung */}
              {term.usage && (
                <div className="card card-pad-lg">
                  <h3 className="h3">Verwendung</h3>
                  <p style={{ marginTop: 10, lineHeight: 1.75 }}>{term.usage}</p>
                </div>
              )}

              <div>
                <Link to="/glossar" className="btn btn-secondary">
                  <LuArrowLeft size={16} aria-hidden="true" /> Zurück zum Glossar
                </Link>
              </div>
            </div>

            <aside className="stack-lg">
              {/* Verwandte Begriffe */}
              {term.relatedTerms && term.relatedTerms.length > 0 && (
                <div className="card card-pad">
                  <h3 className="h4">Verwandte Begriffe</h3>
                  <div className="row" style={{ gap: 8, marginTop: 14 }}>
                    {term.relatedTerms.map(relatedSlug => {
                      const relatedTerm = getTermBySlug(relatedSlug);
                      return relatedTerm ? (
                        <Link key={relatedSlug} to={`/glossar/${relatedSlug}`} className="pill-link mono">
                          {relatedTerm.term}
                        </Link>
                      ) : null;
                    })}
                  </div>
                </div>
              )}
              {/* Tools */}
              {term.relatedTools && term.relatedTools.length > 0 && (
                <div className="card card-pad">
                  <h3 className="h4">Passende Tools</h3>
                  <p className="small muted" style={{ marginTop: 4 }}>
                    Probiere diese Tools aus, um mit {term.term} zu arbeiten:
                  </p>
                  <div style={{ display: "grid", gap: 8, marginTop: 14 }}>
                    {term.relatedTools.map((toolPath, idx) => {
                      const to = TOOL_ALIASES[toolPath] || toolPath;
                      const tool = TOOLS.find(t => t.to === to);
                      const extra = EXTRA_TOOLS[to];
                      const Icon = tool?.icon || extra?.icon;
                      const name = tool ? `EDIFACT ${tool.name}` : extra?.name || to;
                      return (
                        <Link key={idx} to={to} className="card card-link" style={{ flexDirection: "row", alignItems: "center", gap: 12, padding: "10px 12px", borderRadius: 12 }}>
                          {Icon && <span className="icon-tile" style={{ width: 32, height: 32, fontSize: 16, borderRadius: 8 }}><Icon aria-hidden="true" /></span>}
                          <span className="strong small" style={{ flex: 1 }}>{name}</span>
                          <LuArrowRight className="card-link-arrow" size={15} aria-hidden="true" />
                        </Link>
                      );
                    })}
                  </div>
                </div>
              )}

            </aside>
          </div>
        </div>
      </section>
    </>
  );
}
